import React, {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {formatPrice, genericMessageHandler, Icon} from 'shared/helpers';
import {
    Row,
    BoldSecondaryText,
    ActionContainer,
} from 'components/customer/Confirmation/Checkout/Table';
import {PartialRoom} from 'shared/types/PartialRoom';
import {useDeleteRoomMutation} from 'components/customer/Room/store/roomApi';
import {PartialJob} from 'shared/types/PartialJob';
import {useNotificationContext} from 'contexts/NotificationContext';
import {useDialogContext} from 'contexts/DialogContext';
import {useJobContext} from 'contexts/JobContext';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';

interface RoomProps {
    room: PartialRoom;
}

export const Room = ({room}: RoomProps) => {
    const {notify} = useNotificationContext();
    const {job} = useJobContext() as PartialJob;
    const {showDialog} = useDialogContext();
    const navigate = useNavigate();
    const [deleteRoom, {isLoading}] = useDeleteRoomMutation();

    const handleRoomEditButton = useCallback(() => {
        void navigate(`/v2/job/${job.displayId}/room/${room.id}/dashboard`);
    }, [navigate, room, job]);

    const handleRoomDeleteButton = useCallback(() => {
        showDialog({
            title: 'Delete Room',
            message: 'Are you sure you want to delete this Room?',
            yes: async () => {
                try {
                    await deleteRoom({
                        id: room.id,
                        jobId: room.jobId,
                    });

                    genericMessageHandler(
                        notify,
                        {message: 'Room deleted'},
                        'success'
                    );
                } catch (error) {
                    genericMessageHandler(notify, {
                        message:
                            'Could not delete the room right now, Please try again later.',
                    });
                }
            },
        });
    }, [showDialog, deleteRoom, room, notify]);

    return (
        <Row key={room.id}>
            <td>{room.name}</td>
            <td>
                <BoldSecondaryText>
                    {formatPrice(room.cost, {})}
                </BoldSecondaryText>
            </td>
            <td>
                {job?.status == JOB_STATUS.PROCESSING ? (
                    <ActionContainer>
                        <Icon
                            onClick={handleRoomEditButton}
                            iconName="Options-Edit.svg"
                            title={`Edit ${room.name}`}
                        />
                        <Icon
                            onClick={handleRoomDeleteButton}
                            style={isLoading ? {opacity: '0.5'} : {}}
                            iconName={
                                isLoading
                                    ? 'tail-spin.svg'
                                    : 'Options-Delete.svg'
                            }
                            title={`Delete ${room.name}`}
                        />
                    </ActionContainer>
                ) : null}
            </td>
        </Row>
    );
};
